import React, { useEffect, useMemo, Suspense, useState } from 'react';
import { Canvas, useThree, useLoader } from '@react-three/fiber';
import {
  OrbitControls,
  Environment,
  ContactShadows,
  CubeCamera,
} from '@react-three/drei';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import DiamondModel from './DiamondModel';

const DIAMOND_HDR_PATH = '/metal_01.hdr';

// Kamera başlangıç pozisyonları — Canvas ilk render'ında doğru pozla açılsın
// (yoksa OrbitControls'ın "flicker" animasyonu oluyor).
const INITIAL_CAMERA_POSITIONS = {
  '3D': [0, 0.72, 1.54],
  'X': [-1.2, 0, 0],
  'Y': [0, 1.3, 0.001],
  'Z': [0, 0, 1.2],
};

const METAL_PARAMS = {
  YellowGold: { color: 0xf7c97a, roughness: 0.18 },
  WhiteGold: { color: 0xf2f2f2, roughness: 0.12 },
  RoseGold: { color: 0xe8b089, roughness: 0.18 },
};

const createMetalMaterial = (metalType) => {
  const params = METAL_PARAMS[metalType] || METAL_PARAMS.WhiteGold;

  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(params.color),
    roughness: params.roughness,
    metalness: 1.0,
    envMapIntensity: 1.1,
  });
};

function MetalLoader({ path, metalType, onLoaded }) {
  const gltf = useLoader(GLTFLoader, path);
  const [model, setModel] = useState(null);

  const metalMaterial = useMemo(() => {
    return createMetalMaterial(metalType);
  }, [metalType]);

  useEffect(() => {
    if (gltf && gltf.scene && metalMaterial) {
      const cloned = gltf.scene.clone(true);
      cloned.traverse((child) => {
        if (child.isMesh) {
          child.material = metalMaterial;
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      if (onLoaded) onLoaded(cloned);
      setModel(cloned);
    }
  }, [gltf, metalMaterial, onLoaded]);

  useEffect(() => {
    if (model && metalMaterial) {
      model.traverse((child) => {
        if (child.isMesh) {
          child.material = metalMaterial;
        }
      });
    }
  }, [metalMaterial, model]);

  return model ? <primitive object={model} /> : null;
}


function DiamondRig({
  mainStonePath,
  hlStonePath,
  bandStonePath,
  showMain,
  showHl,
  showBandStones,
}) {
  const hdr = useLoader(RGBELoader, DIAMOND_HDR_PATH);

  useEffect(() => {
    if (hdr) {
      hdr.mapping = THREE.EquirectangularReflectionMapping;
    }
  }, [hdr]);

  return (
    <CubeCamera resolution={256} frames={Infinity} envMap={hdr} near={0.1} far={100}>
      {(texture) => (
        <>
          {showMain && mainStonePath && (
            <DiamondModel path={mainStonePath} envMap={texture} scale={30} />
          )}
          {showHl && hlStonePath && (
            <DiamondModel path={hlStonePath} envMap={texture} scale={30} />
          )}
          {showBandStones && bandStonePath && (
            <DiamondModel path={bandStonePath} envMap={texture} scale={30} />
          )}
        </>
      )}
    </CubeCamera>
  );
}

function SceneContent({
  cameraType = '3D',
  stoneType,
  stoneSize,
  stoneCode,
  yuvaType,
  yuvaMetal = 'WhiteGold',
  bandType,
  bandMetal = 'WhiteGold',
  showBand = false,
  showStone = false,
  showSetting = false,
}) {
  const { camera } = useThree();

  // 3D başlangıç: yaklaşık 25° elevasyon (taht/görücü açısı),
  // mesafe 1.7 birim → OrbitControls min/max (1.0–2.5) aralığında güvenli.
  //   y = 1.7 * cos(65°) ≈ 0.72
  //   z = 1.7 * sin(65°) ≈ 1.54
  const cameraPositions = {
    '3D': {
      position: new THREE.Vector3(0, 0.72, 1.54),
      target: new THREE.Vector3(0, 0, 0),
    },
    'X': {
      position: new THREE.Vector3(-1.2, 0, 0),
      target: new THREE.Vector3(0, 0, 0),
    },
    'Y': {
      position: new THREE.Vector3(0, 1.3, 0.001),
      target: new THREE.Vector3(0, 0, 0),
    },
    'Z': {
      position: new THREE.Vector3(0, 0, 1.2),
      target: new THREE.Vector3(0, 0, 0),
    },
  };

  useEffect(() => {
    const config = cameraPositions[cameraType];
    if (config) {
      camera.position.copy(config.position);
      camera.lookAt(config.target);
      camera.updateProjectionMatrix();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraType, camera]);

  // Yol hesaplamaları: ilgili tüm anahtarlar varsa geçerli.
  const mainStonePath = showStone
    ? `/models/yuvalar/${stoneType}/${stoneSize}/ST/ST.glb`
    : null;
  const hlStonePath = showStone
    ? `/models/yuvalar/${stoneType}/${stoneSize}/ST/HL.glb`
    : null;
  const yuvaPath = showSetting && stoneCode
    ? `/models/yuvalar/${stoneType}/${stoneSize}/OO/${stoneType}-${yuvaType}-${stoneCode}.glb`
    : null;
  const bandPath = showBand ? `/models/kollar/OO/${bandType}.glb` : null;
  const bandStonePath = showBand ? `/models/kollar/ST/${bandType}.glb` : null;

  const needHlStones = !!(showStone && yuvaType && yuvaType.includes('HL'));
  const needBandStones = !!(
    showBand && bandType && ['5', '6', '7', '8'].some((v) => bandType.includes(v))
  );

  return (
    <>
      {/* Tek kaynak IBL — diamond sheen/specular siyah olduğu için IBL yükü taşıyor */}
      <Environment preset="studio" background={false} environmentIntensity={0.75} />

      {/* Üst ışık yumuşatıldı; "güneş" parıltısını engellemek için intensity düşürüldü
          ve penumbra artırıldı (daha soft edge). */}
      <spotLight
        position={[3, 4, 2]}
        intensity={0.9}
        angle={0.55}
        penumbra={0.85}
        decay={2}
        distance={15}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      <directionalLight position={[-3, 2, 2]} intensity={0.4} />
      <spotLight
        position={[0, 2.5, -3]}
        intensity={0.55}
        angle={0.6}
        penumbra={0.7}
        decay={2}
        distance={12}
        color="#fff5e6"
      />
      <hemisphereLight args={['#ffffff', '#e8e8e8', 0.3]} />

      <Suspense fallback={null}>
        {/* Metal parçaları CubeCamera'nın DIŞINDA — böylece elmasta yansıyabilirler */}
        {yuvaPath && (
          <MetalLoader
            metalType={yuvaMetal}
            path={yuvaPath}
            onLoaded={(model) => {
              model.scale.set(30, 30, 30);
            }}
          />
        )}

        {bandPath && (
          <MetalLoader
            metalType={bandMetal}
            path={bandPath}
            onLoaded={(model) => {
              model.scale.set(30, 30, 30);
            }}
          />
        )}

        {/* Elmaslar: yalnızca taş seçildiyse yüklenir. CubeCamera çevreyi yakalar. */}
        {(showStone || needBandStones) && (
          <DiamondRig
            mainStonePath={mainStonePath}
            hlStonePath={hlStonePath}
            bandStonePath={bandStonePath}
            showMain={showStone}
            showHl={needHlStones}
            showBandStones={needBandStones}
          />
        )}
      </Suspense>

      {/* Zemin tutturma — yumuşak contact shadow */}
      <ContactShadows
        position={[0, -0.45, 0]}
        opacity={0.35}
        scale={4}
        blur={2.5}
        far={1}
        resolution={1024}
        color="#000000"
      />

      {/* Bloom: sadece çok seçici bir şekilde elmastan gelen iğne parıltılarını yakalar.
          threshold 0.98 → sadece HDR range'de >1.0 değerli pikseller → metal/ışık bulamacı yok.
          intensity düşük + radius küçük → "güneş diski" yerine yıldız pırıltısı. */}
      <EffectComposer disableNormalPass multisampling={0}>
        <Bloom
          intensity={0.35}
          luminanceThreshold={0.98}
          luminanceSmoothing={0.05}
          mipmapBlur
          radius={0.45}
        />
      </EffectComposer>

      <OrbitControls
        enabled={!(cameraType === 'X' || cameraType === 'Y' || cameraType === 'Z')}
        enableDamping
        dampingFactor={0.1}
        minDistance={1.0}
        maxDistance={1.8}
        zoomSpeed={0.8}
        mouseButtons={{
          LEFT: THREE.MOUSE.ROTATE,
          MIDDLE: null,
          RIGHT: null,
        }}
        enablePan={false}
        autoRotate
        autoRotateSpeed={1.5}
        // Açısal limitler:
        //   minPolarAngle = π/6  → kamera +Y ekseninden en fazla 30° uzak → yani
        //   yüzüğün tam tepesinden dik bakmayı engeller, yukarıda ~60° limiti.
        //   maxPolarAngle ≈ 0.62π → yatayın hafif altını görebilir, yuvanın
        //   altına geçip ters çevirmeyi engeller.
        minPolarAngle={Math.PI / 6}
        maxPolarAngle={Math.PI * 0.62}
      />

    </>
  );
}

function ThreeScene(props) {
  const initialCameraPosition =
    INITIAL_CAMERA_POSITIONS[props.cameraType] || INITIAL_CAMERA_POSITIONS['3D'];

  return (
    <Canvas
      shadows
      style={{ display: 'block', width: '100%', height: '100%', touchAction: 'none' }}
      gl={{
        antialias: true,
        alpha: false,
        preserveDrawingBuffer: true,
        powerPreference: 'high-performance',
        outputColorSpace: THREE.SRGBColorSpace,
        toneMapping: THREE.ACESFilmicToneMapping,
        toneMappingExposure: 0.85,
        shadowMap: { enabled: true, type: THREE.PCFSoftShadowMap },
      }}
      onCreated={({ gl }) => {
        gl.setClearColor('#f6f6f6', 1);
      }}
      camera={{
        fov: 55,
        near: 0.01,
        far: 100,
        position: initialCameraPosition,
      }}
      dpr={[1, 2]}
    >
      <SceneContent {...props} />
    </Canvas>
  );
}

export default ThreeScene;
